/**
 * The artwork picker — pick from the library, or upload a new file and attach
 * it in one step (CAE-1552).
 *
 * Lifted out of `contest-reel-options-panel.tsx` rather than rebuilt. The design
 * does not draw this dialog at all: the handoff's prototype mocks the upload with
 * `onClick={onUploaded}`, so there is no spec to rebuild it against, and the one
 * that exists is the only thing in the admin that writes
 * `reel_options.asset_id`. CAE-1552's first acceptance criterion asks that asset
 * attach be preserved, and this is what preserving it looks like.
 *
 * **Shared since CAE-1595**, which gave a Sport's clubs their Team Logo and
 * Generic Jersey. MOD-11's approach puts club images "through the same asset
 * picker the reel options panel already uses, so club images need no new
 * image-picking machinery" — so it moved out of `pages/contests/edit/reels/`
 * and `pickName` became `subject`, the only thing in it that was ever about a
 * Reel Option. It still owns no mutation: the caller attaches, and the caller
 * decides what that means.
 *
 * **The caller owns what "still the right target" means.** The dialog holds no
 * idea of what it is attaching to beyond `subject`; the guard against
 * attaching to a target that has moved out from under it belongs to whoever
 * opened it. Edit › Reels does that by remembering the Reel its row was on and
 * refusing an attach once the rail has moved (the `assetTarget.reelNumber`
 * check in `contest-edit-reels-step.tsx`); the club roster needs no such guard,
 * because its rows do not move.
 *
 * **It is not the only asset picker in here.** `asset-picker-modal.tsx` does
 * the same job for a Contest's lobby images, and the two should probably be
 * one — raised on CAE-1595 and deliberately not done there, since collapsing
 * them touches a screen that ticket had no reason to open.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MuiCheckIcon from '@mui/icons-material/Check';
import MuiCloseIcon from '@mui/icons-material/Close';
import { assetsApi, type Asset } from '../api/assets';
import { toSafePreviewUrl } from '../utils/safe-url';
import { getPrefixedErrorMessage } from '../utils/error-message';

type PickerTab = 'upload' | 'library';

export interface AssetPickerDialogProps {
  open: boolean;
  /**
   * What is being given artwork, named as the operator knows it — a Reel
   * Option's Pick Name, or "Boston Celtics' jersey". The dialog's copy, and
   * nothing else.
   */
  subject: string;
  onClose: () => void;
  /** Attaches the chosen asset. The caller owns the mutation and its notice. */
  onAttach: (assetId: string) => void;
  isAttaching: boolean;
  onNotice: (message: string) => void;
}

export const AssetPickerDialog = ({
  open,
  subject,
  onClose,
  onAttach,
  isAttaching,
  onNotice,
}: AssetPickerDialogProps) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<PickerTab>('library');
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const libraryQuery = useQuery({
    queryKey: ['admin', 'assets', search, tagFilter],
    queryFn: () => assetsApi.list({ query: search || undefined, tag: tagFilter || undefined }),
    enabled: open && tab === 'library',
  });

  /*
   * The whole library, for the tag dropdown's options — and only once the
   * dialog is open.
   *
   * It had no `enabled` guard, which did not show while the only caller was
   * Edit › Reels, whose own `'all-for-reels'` read is the same request under
   * the same key. Mounting it on the Sports page made it a read nobody asked
   * for: open the Clubs tab and the browser fetches every asset in the library
   * to populate a dropdown inside a dialog that may never open — against the
   * frugality `clubsVisited` exists for on that very page. Raised on CAE-1595.
   */
  const allAssetsQuery = useQuery({
    queryKey: ['admin', 'assets', 'all-for-reels'],
    queryFn: () => assetsApi.list({}),
    enabled: open,
  });

  const tags = useMemo(() => {
    const set = new Set<string>();
    for (const asset of allAssetsQuery.data ?? []) for (const tag of asset.tags) set.add(tag);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allAssetsQuery.data]);

  /**
   * Cleared whenever the dialog closes — on *every* route out, not just the ones
   * this component owns.
   *
   * It stays mounted across opens (only `open` flips), so state left standing
   * reappears on the next Reel Option's picker. Resetting inside `close()` alone
   * covered Cancel, the X and the backdrop, and missed the one path that matters
   * most: a successful attach, which the caller closes by clearing its own
   * target. The next row's slot then reopened with the previous row's asset
   * still marked *Selected* and **Attach asset** already enabled — one click
   * from silently giving row two the artwork picked for row one — and on the
   * Upload tab, with the previous file still staged, re-uploaded it as a
   * duplicate. The panel this replaced reset on every success path; this is that
   * guarantee restored, in the one place no exit can get past. Caught in review.
   */
  useEffect(() => {
    if (open) return;
    setTab('library');
    setSearch('');
    setTagFilter('');
    setSelectedAssetId(null);
    setUploadName('');
    setUploadTags('');
    setUploadFile(null);
  }, [open]);

  /*
   * Upload then attach, in that order and as one action from the operator's
   * side: a file uploaded but left unattached is an orphan in the library that
   * nobody asked for.
   */
  const upload = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('Pick a file first.');
      return assetsApi.upload({
        file: uploadFile,
        name: uploadName.trim() || uploadFile.name,
        tags: uploadTags
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
      });
    },
    onSuccess: async (asset) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'assets'] });
      onAttach(asset.id);
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to upload asset', error)),
  });

  /*
   * No exit while a write is in flight — which is what makes "upload then
   * attach" one action rather than two.
   *
   * Cancel, the X and the backdrop were all live during the upload, and the
   * caller's attach handler opens `if (!assetTarget) return;`. Closing between
   * the two halves therefore uploaded the file, dropped the attach without a
   * notice, and left in the library exactly the orphan this ordering exists to
   * prevent. Shutting the exits is the honest fix: the operator asked for both
   * halves, and there is no half of this worth offering to abandon.
   */
  const busy = upload.isPending || isAttaching;
  const closeIfIdle = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closeIfIdle}
      fullWidth
      maxWidth="md"
      aria-labelledby="asset-picker-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            maxHeight: 'min(900px, 92vh)',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle sx={{ pt: 2.5, px: 3, pb: 1 }} id="asset-picker-title">
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <Box>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              Attach asset
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 480 }}>
              Select from the library or upload a new image for {subject}.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
            size="small"
            sx={{ color: 'text.secondary', mt: -0.25 }}
          >
            <MuiCloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, value: PickerTab) => {
          setTab(value);
          setSelectedAssetId(null);
        }}
        sx={{ px: 3, minHeight: 44, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="upload" label="Upload" disableRipple />
        <Tab value="library" label="Library" disableRipple />
      </Tabs>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {tab === 'upload' ? (
          <Stack spacing={2} sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <TextField
              size="small"
              label="Asset name"
              value={uploadName}
              onChange={(event) => setUploadName(event.target.value)}
            />
            <TextField
              size="small"
              label="Tags (comma separated)"
              value={uploadTags}
              onChange={(event) => setUploadTags(event.target.value)}
            />
            <Button component="label" variant="outlined" sx={{ alignSelf: 'flex-start' }}>
              {uploadFile ? `File: ${uploadFile.name}` : 'Choose file'}
              <input
                type="file"
                hidden
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(event) => setUploadFile(event.target.files?.[0] ?? null)}
              />
            </Button>
          </Stack>
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ px: 3, pt: 2.5, pb: 2, flexShrink: 0 }}
            >
              <TextField
                size="small"
                placeholder="Search assets by name or tag…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                fullWidth
                slotProps={{ htmlInput: { 'aria-label': 'Search assets' } }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel id="asset-tag-filter-label">Filter by tag</InputLabel>
                <Select
                  labelId="asset-tag-filter-label"
                  label="Filter by tag"
                  value={tagFilter}
                  onChange={(event) => setTagFilter(event.target.value)}
                >
                  <MenuItem value="">
                    <em>All tags</em>
                  </MenuItem>
                  {tags.map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 240, overflow: 'auto', px: 3, pb: 2 }}>
              {libraryQuery.isLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading assets…
                </Typography>
              ) : (libraryQuery.data ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No assets match your search. Try another term or upload a new image.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(1, minmax(0, 1fr))',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  {(libraryQuery.data ?? []).map((asset: Asset) => {
                    const previewUrl = toSafePreviewUrl(asset.previewUrl);
                    const isSelected = selectedAssetId === asset.id;

                    return (
                      <Paper
                        key={asset.id}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 1.5, borderWidth: isSelected ? 2 : 1 }}
                      >
                        <Stack spacing={1.25}>
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              aspectRatio: '1 / 1',
                              maxHeight: 160,
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'grid',
                              placeItems: 'center',
                            }}
                          >
                            {previewUrl ? (
                              <Box
                                component="img"
                                src={previewUrl}
                                alt=""
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No preview
                              </Typography>
                            )}
                            {isSelected ? (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  display: 'grid',
                                  placeItems: 'center',
                                }}
                              >
                                <MuiCheckIcon sx={{ fontSize: 18, color: 'common.white' }} />
                              </Box>
                            ) : null}
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {asset.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.25 }}
                            >
                              {asset.tags.length > 0 ? asset.tags.join(', ') : 'No tags'}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            fullWidth
                            variant={isSelected ? 'contained' : 'outlined'}
                            disableElevation
                            disabled={isAttaching}
                            /*
                             * Named after the asset, not "Select": a grid of
                             * buttons all reading the same word is a list a
                             * screen reader cannot choose from.
                             */
                            aria-label={
                              isSelected ? `${asset.name} selected` : `Select ${asset.name}`
                            }
                            onClick={() =>
                              setSelectedAssetId((previous) =>
                                previous === asset.id ? null : asset.id,
                              )
                            }
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}
      >
        <Typography variant="body2" color="text.secondary">
          {tab === 'library'
            ? `${selectedAssetId ? 1 : 0} asset selected`
            : `${uploadFile ? 1 : 0} file ready`}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {tab === 'library' ? (
            <Button
              variant="contained"
              disableElevation
              disabled={!selectedAssetId || busy}
              onClick={() => {
                if (selectedAssetId) onAttach(selectedAssetId);
              }}
            >
              Attach asset
            </Button>
          ) : (
            <Button
              variant="contained"
              disableElevation
              disabled={!uploadFile || busy}
              onClick={() => upload.mutate()}
            >
              Upload and attach
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
